import { useContext, useEffect, useRef } from "react";
import { AuthContext } from "@/context/AuthContext";
import useCurrentUserQuery from "@/api/query/useCurrentUserQuery";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

export function useUser() {
  const { i18n } = useTranslation();
  const { setAuthUser, authUser } = useContext(AuthContext);
  const fetchingUser = useRef(false);
  const navigate = useNavigate();

  const { data, refetch, isFetching, isFetched, isError, error } =
    useCurrentUserQuery();

  // Trigger fetch if user is not in state
  useEffect(() => {
    if (!authUser) {
      refetch();
    }
  }, [authUser, refetch]);

  useEffect(() => {
    if (isFetching) {
      fetchingUser.current = true;
    }

    if (isFetched && !isFetching && fetchingUser.current) {
      fetchingUser.current = false;
      const user = data?.data?.data ? data.data.data : null;
      setAuthUser(user);

      if (user) {
        // Handle successful authentication
        i18n.changeLanguage(user.language?.toLowerCase());
        localStorage.setItem(
          "organisation-id",
          user.userOrganisation[0]?.organisationId
        );

        if (!user.isVerified && !window.location.pathname.includes("collaborator")) {
          navigate("/verify-email");
        }
      } else {
        // Redirect to login only on actual auth failure (e.g. 400 Bad Request on refresh, 401 Unauthorized, or 403 Forbidden)
        const status = error?.response?.status;
        if (isError && (status === 400 || status === 401 || status === 403)) {
          navigate("/login");
        }
      }
    }
  }, [
    isFetching,
    isFetched,
    data,
    isError,
    error,
    setAuthUser,
    i18n,
    navigate
  ]);

  return { user: authUser, isError };
}
